function _checkPrivateRedeclaration(obj, privateCollection) {
    if (privateCollection.has(obj)) {
        throw new TypeError("Cannot initialize the same private elements twice on an object");
    }
}
function _classApplyDescriptorGet(receiver, descriptor) {
    if (descriptor.get) {
        return descriptor.get.call(receiver);
    }
    return descriptor.value;
}
function _classApplyDescriptorSet(receiver, descriptor, value) {
    if (descriptor.set) {
        descriptor.set.call(receiver, value);
    } else {
        if (!descriptor.writable) {
            throw new TypeError("attempted to set read only private field");
        }
        descriptor.value = value;
    }
}
function _classExtractFieldDescriptor(receiver, privateMap, action) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to " + action + " private field on non-instance");
    }
    return privateMap.get(receiver);
}
function _classPrivateFieldGet(receiver, privateMap) {
    var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "get");
    return _classApplyDescriptorGet(receiver, descriptor);
}
function _classPrivateFieldInit(obj, privateMap, value) {
    _checkPrivateRedeclaration(obj, privateMap);
    privateMap.set(obj, value);
}
function _classPrivateFieldSet(receiver, privateMap, value) {
    var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "set");
    _classApplyDescriptorSet(receiver, descriptor, value);
    return value;
}
/**
 * Manages, creates and authorizes a DID session key for a user. Returns an authenticated DIDs instance
 * to be used in other Ceramic libraries. Supports did:pkh for blockchain accounts with Sign-In with
 * Ethereum and CACAO for authorization.
 *
 * ## Installation
 *
 * ```sh
 * npm install @glazed/did-session
 * ```
 *
 * ## Usage
 *
 * Create an instance, authorize and use DIDs where needed.
 *
 * ```ts
 * import { DIDSession } from '@glazed/did-session'
 * import { EthereumAuthProvider } from '@ceramicnetwork/blockchain-utils-linking'
 *
 * const ethProvider = // import/get your web3 eth provider
 * const addresses = await ethProvider.enable()
 * const authProvider = new EthereumAuthProvider(ethProvider, addresses[0])
 *
 * const session = await DIDSession.create({ authProvider })
 * const did = await session.authorize()
 *
 * // Uses DIDs in ceramic & glaze libraries, ie
 * const ceramic = new CeramicClient()
 * ceramic.did = did
 *
 * ```
 *
 * @module did-session
 */ import { Ed25519Provider } from 'key-did-provider-ed25519';
import KeyDidResolver from 'key-did-resolver';
import { randomBytes } from '@stablelib/random';
import { DID } from 'dids';
export async function createDIDKey(seed) {
    const didProvider = new Ed25519Provider(seed || randomBytes(32));
    const didKey = new DID({
        provider: didProvider,
        resolver: KeyDidResolver.getResolver()
    });
    await didKey.authenticate();
    return didKey;
}
var _authProvider = /*#__PURE__*/ new WeakMap(), _did = /*#__PURE__*/ new WeakMap();
/**
 * DID Session
 *
 * ```sh
 * import { DIDSession } from '@glazed/did-session'
 * ```
 */ export class DIDSession {
    /**
   * Get authProvider
   */ get authProvider() {
        return _classPrivateFieldGet(this, _authProvider);
    }
    /**
   * Request authorization for session
   */ async authorize(capabilityOpts = {}) {
        const didKey = await createDIDKey();
        // Pass through opts resources instead, resource arg does not support anything but streamids at moment
        const opts = Object.assign({
            resources: [
                `ceramic://*`
            ]
        }, capabilityOpts);
        const cacao = await _classPrivateFieldGet(this, _authProvider).requestCapability(didKey.id, [], opts);
        const didWithCap = didKey.withCapability(cacao);
        await didWithCap.authenticate();
        _classPrivateFieldSet(this, _did, didWithCap);
        return didWithCap;
    }
    /**
   * Get DID instance, if authorized
   */ getDID() {
        if (!_classPrivateFieldGet(this, _did)) {
            throw new Error('DID not available, has not authorized');
        }
        return _classPrivateFieldGet(this, _did);
    }
    /** DID string associated to the session instance. session.id == session.getDID().parent */ get id() {
        if (!_classPrivateFieldGet(this, _did)) {
            throw new Error('ID not available, has not authorized');
        }
        return _classPrivateFieldGet(this, _did).parent;
    }
    constructor(params){
        _classPrivateFieldInit(this, _authProvider, {
            writable: true,
            value: void 0
        });
        _classPrivateFieldInit(this, _did, {
            writable: true,
            value: void 0
        });
        _classPrivateFieldSet(this, _authProvider, params.authProvider);
    }
}
